import { useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { Form, ErrorMessage } from "@/shared/components";
// import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { useSnackbar } from "notistack";
import { CrudDialogHeader } from "./CrudDialogHeader";

export interface CrudEditFormProps<Row = any> {
  id: string;
  schema: any;
  texts: {
    itemUpdated: string;
    unsavedChangesWarning?: string;
  };
  editQuery: any;
  updateMutation: any;
  getEditData: (data: any) => Row;
  refetchQueries?: any;
  onClose?: () => void;
  title?: string;
  subTitle?: string;
  getConfirm?: (params: { values: Record<string, any> }) => any;
  getTitle?: (data: any) => string | null;
  onSubmitSuccess?: (result: any) => void;
  customHeaderButton?: React.ReactNode;
  paper?: boolean;
  // Предупреждения о несохраненных изменениях
  warnOnUnsavedChanges?: boolean;
  unsavedChangesWarningMessage?: string;
}

export function CrudEditForm<Row = any>({
  id,
  schema,
  texts,
  editQuery,
  updateMutation,
  getEditData,
  refetchQueries,
  onClose,
  title,
  subTitle,
  getConfirm,
  getTitle = () => null,
  onSubmitSuccess: _onSubmitSuccess,
  customHeaderButton,
  paper,
  warnOnUnsavedChanges,
  unsavedChangesWarningMessage,
}: CrudEditFormProps<Row>) {
  const { enqueueSnackbar } = useSnackbar();

  const editQueryResult = useQuery(editQuery, {
    fetchPolicy: "network-only",
    variables: { id },
    skip: !id,
  });

  const [updateItem] = useMutation(updateMutation, {
    refetchQueries,
    onError: (error) => {
      /**
       * вызовем Snackbar что бы было видно ошибку если мы просролили форму
       */
      if (error.graphQLErrors.length) {
        enqueueSnackbar(
          error.graphQLErrors.map((err: any) => err.message).join(", "),
          { variant: "error" }
        );
      }
      throw error;
    },
  });

  const initialValues = useMemo(
    () => (editQueryResult.data ? getEditData(editQueryResult.data) : null),
    [editQueryResult.data] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Обработка динамической схемы
  const resolvedSchema = useMemo(
    () =>
      editQueryResult.data && typeof schema === "function"
        ? schema(editQueryResult.data)
        : schema,
    [editQueryResult.data] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSubmitSuccess = (result: any) => {
    enqueueSnackbar(texts.itemUpdated, { variant: "success" });

    editQueryResult.refetch();

    if (_onSubmitSuccess) {
      _onSubmitSuccess(result);
    }
  };

  if (editQueryResult.error) {
    return <ErrorMessage error={editQueryResult.error} />;
  }

  if (!editQueryResult) return <LinearProgress />;
  if (editQueryResult.loading) return <LinearProgress />;
  if (!editQueryResult.data || !initialValues) return <LinearProgress />;

  return (
    <Form
      initialValues={initialValues as Record<string, any>}
      schema={resolvedSchema}
      onSubmit={(input: any) => updateItem({ variables: { id, input } })}
      onSubmitSuccess={onSubmitSuccess}
      submitButton={false}
      resetButton={false}
      objectId={id}
      editData={editQueryResult.data}
      getConfirm={getConfirm}
      warnOnUnsavedChanges={warnOnUnsavedChanges}
      paper={paper !== false}
      unsavedChangesWarningMessage={
        unsavedChangesWarningMessage || texts.unsavedChangesWarning
      }
      header={({ disabled }: { disabled: boolean }) => (
        <CrudDialogHeader
          position="absolute"
          saveButton
          onClose={onClose}
          secondary={title}
          primary={subTitle}
          title={getTitle(editQueryResult.data)}
          disabled={disabled}
          customHeaderButton={customHeaderButton}
        />
      )}
    />
  );
}
